import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Divider,
  Empty,
  Row,
  Space,
  Tree,
  Input,
  Tag,
  Radio,
} from 'antd';
import React, { useCallback, useContext, useMemo } from 'react';

import { useMemoizeObject, useWindowSize } from '@app/hooks';
import { CHF_VISITS_FILTER } from '@app/pages/Clients/ClientsList/constants/filterKeysAndIds';
import { ClientFiltersContext } from '@app/providers';

import { hideFilterBlock } from '@pages/Clients/ClientsList/utils';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { DataNode } from 'antd/es/tree';
import type { RadioChangeEvent } from 'antd/lib';
import type { ReactElement } from 'react';

const { Search } = Input;

type Props = { filterGroupIndex: number };

export const VisitsTags = ({ filterGroupIndex }: Props) => {
  const {
    filters,
    allRecordTags,
    recordSearchedTags,
    allRecordTagsWithoutAdaptation,
    setRecordSearchedTags,
    setServerFilters,
    setFilters,
  } = useContext(ClientFiltersContext);

  const { innerWidth } = useWindowSize();

  const memoizedFilters = useMemoizeObject(filters);

  const checkedKeys = useMemo(() => {
    const ids =
      memoizedFilters?.[CHF_VISITS_FILTER]?.[filterGroupIndex]?.Values?.[0]
        ?.TagIds ?? null;

    if (!ids || ids.length === 0) return [];
    return ids.map((id: number) => `parent-${id}`);
  }, [memoizedFilters, filterGroupIndex]);

  const extractData = useCallback(
    (key: string) => {
      const id = Number(key.split('-')[1]);
      return allRecordTagsWithoutAdaptation.find(({ Id }) => Id === id);
    },
    [allRecordTagsWithoutAdaptation]
  );

  const onSearchChange: React.ChangeEventHandler<HTMLInputElement> =
    useCallback(
      (event) => {
        const query = event.target?.value.toLowerCase().trim() || '';

        const next = allRecordTags.filter((tag: DataNode) => {
          const { title } = tag;

          const searchTitle =
            'props' in (title as ReactElement)
              ? (title as ReactElement)?.props?.title
              : 'Name' in tag
              ? tag.Name
              : '';

          return String(searchTitle).trim().toLowerCase().includes(query);
        });

        setRecordSearchedTags(next);
      },
      [allRecordTags, setRecordSearchedTags]
    );

  const applyTagIds = useCallback(
    (tagIds: number[]) => {
      setFilters((prev) => {
        const nextVisits = prev.VisitsFilter.map((unit, index) => {
          if (index !== filterGroupIndex) return unit;
          return {
            ...unit,
            Values: [
              {
                ...unit.Values?.[0],
                TagIds: tagIds.length > 0 ? tagIds : null,
              },
            ],
          };
        });

        return { ...prev, VisitsFilter: nextVisits };
      });
    },
    [filterGroupIndex, setFilters]
  );

  const onTreeCheck = useCallback(
    (keys: React.Key[]) => {
      const ids = keys.map((key) => Number(String(key).split('-')[1]));
      applyTagIds(ids);
    },
    [applyTagIds]
  );

  const onSave = useCallback(() => {
    setServerFilters(filters);
    hideFilterBlock('.visits-tags-label', innerWidth, filterGroupIndex);
  }, [filters, filterGroupIndex, innerWidth, setServerFilters]);

  const isSaveDisabled = useMemo(() => {
    const ids =
      memoizedFilters?.[CHF_VISITS_FILTER]?.[filterGroupIndex]?.Values?.[0]
        ?.TagIds ?? null;
    return ids === null;
  }, [memoizedFilters, filterGroupIndex]);

  const handleIncludingTagsChange = useCallback(
    (e: RadioChangeEvent) => {
      const { value } = e.target;
      const isInclude = value === 'include';

      setFilters((prev) => {
        const updatedVisitsFilter = prev.VisitsFilter.map((unit, index) =>
          index === filterGroupIndex
            ? {
                ...unit,
                IsInclude: isInclude,
              }
            : unit
        );

        return { ...prev, VisitsFilter: updatedVisitsFilter };
      });
    },
    [filterGroupIndex, setFilters]
  );

  return (
    <div onClick={(e) => e.stopPropagation()} className="tags-filter">
      <Space direction="vertical">
        <Radio.Group
          defaultValue={
            memoizedFilters?.[CHF_VISITS_FILTER]?.[filterGroupIndex]?.IsInclude
              ? 'include'
              : 'exclude'
          }
          onChange={handleIncludingTagsChange}
        >
          <Space direction="vertical">
            <Radio value="include">Имеет теги</Radio>
            <Radio value="exclude">Не имеет тегов</Radio>
          </Space>
        </Radio.Group>

        <div>
          <Search
            placeholder="Поиск по тегам"
            type="text"
            onChange={onSearchChange}
            className="search-field"
          />

          {recordSearchedTags.length > 0 ? (
            <Tree
              className="tags-tree"
              checkable
              checkedKeys={checkedKeys}
              onCheck={(_, { checkedNodes }) => {
                const keys = checkedNodes.map(({ key }) => key);
                onTreeCheck(keys);
              }}
              treeData={recordSearchedTags.map(({ key, title, children }) => ({
                key,
                title: (
                  <Tag
                    color={extractData(String(key))?.Color}
                    icon={
                      <FontAwesomeIcon
                        icon={extractData(String(key))?.IconId as IconProp}
                        className="tags-icon"
                      />
                    }
                    className="flex ai-center"
                    bordered={false}
                  >
                    {title as never}
                  </Tag>
                ),
                children,
              }))}
            />
          ) : (
            <Empty />
          )}

          <Divider className="tags-divider" />
          <Row justify="end">
            <Button
              type="primary"
              size="small"
              disabled={isSaveDisabled}
              onClick={onSave}
            >
              Выбрать
            </Button>
          </Row>
        </div>
      </Space>
    </div>
  );
};
